// Pricing tier component for ceramic coating landing pages
import { Check, Flame, Star } from "lucide-react";
import { Button } from "./ui/button";

export default function PricingTiers({ city = "Mississauga" }) {
  const tiers = [
    {
      name: "Paint Correction",
      tag: "Stage 1",
      price: "$275",
      sub: "From",
      desc: "Multi-stage hand machine polishing. removes swirl marks, scratches and oxidation. The foundation under any real ceramic.",
      bullets: [
        "1-stage machine polishing",
        "Paint depth measured per panel",
        "Documented before/after report",
        "Up to 95% defect removal",
      ],
      featured: false,
      icon: Star,
    },
    {
      name: "Full Ceramic Coating",
      tag: "Spring Special · Limited Spots",
      price: "$550",
      regular: "$1,500",
      sub: "Now",
      desc: "$550 covers materials only. Surface prep, application labor & window coating are FREE when we film your car for our channels. Hand-applied 2-layer Gtechniq. Crystal Serum Light + EXO v5.",
      bullets: [
        "FREE 1-stage surface prep (worth $400+)",
        "FREE application labor (hand-applied)",
        "FREE window coating ($150 value)",
        "Gtechniq Crystal Serum Light base coat",
        "Gtechniq EXO v5 hydrophobic top coat",
        "Written 5-year warranty + before/after report",
      ],
      featured: true,
      icon: Flame,
      tagline: "Content-based offer",
    },
    {
      name: "Full Detail + Coating",
      tag: "Custom Quote",
      price: "Quote",
      sub: "From $800",
      desc: "Heavy correction for severely neglected paint, plus full ceramic coating. Best for older vehicles and exotic finishes.",
      bullets: [
        "2 or 3-stage paint correction",
        "2-layer ceramic coating",
        "Wheels, glass & plastic coated",
        "Climate-controlled cure",
        "Free annual maintenance washes",
      ],
      featured: false,
      icon: Star,
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 md:py-36 bg-[#0c0119] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-acid mb-5 font-label">
           Ceramic Packages · {city}
          </div>
          <h2 className="font-display text-5xl md:text-6xl leading-[0.95]">
            Real packages.<br />
            <span className="text-jester">Real, transparent pricing.</span>
          </h2>
          <p className="mt-5 text-white/65 max-w-2xl mx-auto">
            Limited content-based pricing on our flagship 2-layer ceramic. only for select {city} vehicles we feature on our channels.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {tiers.map((t, i) => {
            const Icon = t.icon;
            return (
              <div
                key={i}
                data-testid={`pricing-tier-${i}`}
                className={`relative rounded-2xl p-8 border transition-all duration-500 hover-lift ${
                  t.featured
                    ? "bg-jester-purple/15 border-acid glow-jester"
                    : "bg-white/[0.02] border-white/10 hover:border-white/20"
                }`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-acid text-[#120127] text-xs uppercase tracking-[0.2em] font-label font-bold flex items-center gap-1.5 whitespace-nowrap">
                    <Flame className="w-3 h-3" /> {t.tag}
                  </div>
                )}
                {!t.featured && (
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-label">
                    {t.tag}
                  </div>
                )}
                <h3 className="font-display text-3xl mt-2">{t.name}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-xs uppercase text-white/50 font-label tracking-[0.2em]">
                    {t.sub}
                  </span>
                  <span className="font-stencil text-4xl text-acid">{t.price}</span>
                  {t.regular && (
                    <span className="font-stencil text-2xl text-white/40 line-through decoration-acid/70 decoration-2 ml-1">
                      {t.regular}
                    </span>
                  )}
                </div>
                {t.tagline && (
                  <div className="mt-1 text-xs text-white/55 font-marker">
                    {t.tagline}
                  </div>
                )}
                <p className="mt-4 text-white/65 leading-relaxed text-sm">{t.desc}</p>
                <ul className="mt-6 space-y-3">
                  {t.bullets.map((b, k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-white/80">
                      <Check className="w-4 h-4 text-acid flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-8 w-full h-12 rounded-full font-label tracking-[0.2em] ${
                    t.featured
                      ? "bg-acid hover:bg-[#bff020] text-[#120127]"
                      : "bg-white/5 hover:bg-jester-purple text-white border border-white/10"
                  }`}
                >
                  <a href="#contact">{t.featured ? "Lock In $550" : "Book Now"}</a>
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-white/45 text-sm mt-10 font-label tracking-[0.15em]">
          All prices in CAD. Final quote depends on vehicle size &amp; paint condition. {city} pickup &amp; delivery available.
        </p>
      </div>
    </section>
  );
}
